import { useEffect, useRef, useState } from "react";
import { Button, Input, Modal } from "antd";
import { GoogleMap, LoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { EnvironmentOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { changeLoader } from "../../redux/reducers/loader";
import globalRequest from "../../utils/global/globalRequest";
import * as Apis from "../../utils/helper/enum";
import config from "../../config";

const extractLatLngFromGoogleMapsUrl = (url = "") => {
  if (!url) return null;

  try {
    let match = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
    if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

    match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

    match = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

    return null;
  } catch (error) {
    return null;
  }
};

const ChangeAddressModal = ({ isOpen, onCancel, onSuccess, orderDetailData }) => {
  const dispatch = useDispatch();
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [mapLinkInput, setMapLinkInput] = useState("");
  const [mapLinkError, setMapLinkError] = useState("");
  const [errors, setErrors] = useState({});
  const [isMapsReady, setIsMapsReady] = useState(false);
  const [addressForm, setAddressForm] = useState({
    lat: null,
    lng: null,
    address: "",
    buildingNumber: "",
    floorApartment: "",
    image: "",
  });

  const setFieldValue = (field, value) => {
    setAddressForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const isGoogleMapsAvailable = () => typeof window !== "undefined" && !!window.google?.maps;

  const updateAddressCoordinates = (lat, lng) => {
    setAddressForm((prev) => ({ ...prev, lat, lng }));
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024) {
      toast.error("File size must be less than 100KB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      try {
        dispatch(changeLoader(true));
        const response = await globalRequest("api/customer/upload-image", "post", { files: base64String }, {}, true);
        if (response?.status === 200) {
          const uploadedUrl = response?.data?.data?.[0]?.path;
          setFieldValue("image", uploadedUrl);
          toast.success("Image uploaded successfully");
        } else {
          toast.error(response?.data?.message || "Failed to upload image");
        }
      } catch (error) {
        toast.error(error?.message || "An error occurred while uploading");
      } finally {
        dispatch(changeLoader(false));
      }
    };

    reader.readAsDataURL(file);
  };

  const reverseGeocode = async (lat, lng) => {
    const geocodingUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${config.GOOGLEMAPAPIKEY}`;
    try {
      const response = await fetch(geocodingUrl);
      const data = await response.json();
      if (data.status === "OK" && data.results?.length) {
        const place = data.results[0];
        const addressComponents = place.address_components || [];
        let streetNumber = "";
        let apartmentNumber = "";

        for (const component of addressComponents) {
          const types = component.types || [];
          if (types.includes("street_number")) streetNumber = component.long_name || "";
          if (types.includes("subpremise")) apartmentNumber = component.long_name || "";
        }

        setAddressForm((prev) => ({
          ...prev,
          address: place.formatted_address || prev.address,
          buildingNumber: streetNumber || prev.buildingNumber,
          floorApartment: apartmentNumber || prev.floorApartment,
        }));
      }
    } catch (error) {
      toast.error(error?.message || "Unable to reverse geocode the selected address");
    }
  };

  const checkAddressInZone = async (lat, lng) => {
    try {
      dispatch(changeLoader(true));
      const response = await globalRequest(
        Apis?.ORDERS?.CHECK_ADDRESS_IN_ZONE,
        "get",
        {},
        { params: { lat, lng } },
        true
      );

      if (response?.status === 200) {
        toast.success("Address is in zone");
        return true;
      }

      toast.error(response?.data?.message || "Address is outside the active delivery zones");
      return false;
    } catch (error) {
      toast.error(error?.message || "Unable to verify the address zone");
      return false;
    } finally {
      dispatch(changeLoader(false));
    }
  };

  const handlePlaceSelect = async () => {
    if (!isGoogleMapsAvailable()) {
      toast.error("Google Maps is still loading. Please try again in a moment.");
      return;
    }

    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    updateAddressCoordinates(lat, lng);
    setFieldValue("address", place.formatted_address || "");
    if (place.geometry.viewport && mapRef.current) {
      mapRef.current.fitBounds(place.geometry.viewport);
    }

    await reverseGeocode(lat, lng);
    await checkAddressInZone(lat, lng);
  };

  const handleMapLinkApply = async () => {
    const trimmedInput = mapLinkInput.trim();
    const isShortenedGoogleMapsUrl = /(?:maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(trimmedInput);
    let coords = isShortenedGoogleMapsUrl ? null : extractLatLngFromGoogleMapsUrl(trimmedInput);
    let addressFromApi = "";
    let buildingNumberFromApi = "";

    if (!coords) {
      try {
        dispatch(changeLoader(true));
        let response = await globalRequest(
          Apis?.ORDERS?.GET_LAT_LNG,
          "get",
          {},
          { params: { url: trimmedInput } },
          true
        );
        let apiResponse = response?.data;
        if (response?.status == 200 && apiResponse?.data) {
          coords = {
            lat: parseFloat(apiResponse?.data?.lat),
            lng: parseFloat(apiResponse?.data?.lng),
          };
          addressFromApi = apiResponse?.data?.address || "";
          buildingNumberFromApi = apiResponse?.data?.buildingNumber || "";
        } else {
          toast.error(apiResponse?.message || "Couldn't find coordinates in this link");
        }
      } catch (e) {
        toast.error(e?.message);
      } finally {
        dispatch(changeLoader(false));
      }
    }

    if (coords && !isNaN(coords.lat) && !isNaN(coords.lng)) {
      setMapLinkError("");
      const targetLat = parseFloat(coords.lat);
      const targetLng = parseFloat(coords.lng);

      setAddressForm((prev) => ({
        ...prev,
        lat: targetLat,
        lng: targetLng,
        ...(addressFromApi ? { address: addressFromApi } : {}),
        ...(buildingNumberFromApi ? { buildingNumber: buildingNumberFromApi } : {}),
      }));

      if (mapRef.current) {
        mapRef.current.panTo({ lat: targetLat, lng: targetLng });
      }

      if (!addressFromApi) {
        await reverseGeocode(targetLat, targetLng);
      }
      await checkAddressInZone(targetLat, targetLng);

      setErrors((prev) => ({ ...prev, address: "" }));
      setMapLinkInput("");
      if (isGoogleMapsAvailable()) {
        setIsMapsReady(true);
      }
    } else {
      setMapLinkError("Please paste a valid Google Maps link");
    }
  };


  const handleSave = async () => {
    const nextErrors = {};

    if (!addressForm.lat || !addressForm.lng) {
      nextErrors.address = "Please select a valid delivery location";
    }
    if (!addressForm.address?.trim()) {
      nextErrors.address = "Address is required";
    }
    if (!addressForm.buildingNumber?.trim()) {
      nextErrors.buildingNumber = "Building Number is required";
    }
    if (!addressForm.floorApartment?.trim()) {
      nextErrors.floorApartment = "Floor / Apartment is required";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const isInZone = await checkAddressInZone(addressForm.lat, addressForm.lng);
    if (!isInZone) return;

    try {
      dispatch(changeLoader(true));
      const response = await globalRequest(
        Apis?.ORDERS?.UPDATE_ADDRESS,
        "post",
        {
          order_id: orderDetailData?.id,
          address_details: {
            ...addressForm,
            address: addressForm.address.trim(),
            buildingNumber: addressForm.buildingNumber.trim(),
            floorApartment: addressForm.floorApartment.trim(),
          },
        },
        {},
        true
      );

      if (response?.status === 200) {
        toast.success(response?.data?.message || "Address updated successfully");
        onSuccess?.({
          lat: addressForm.lat,
          lng: addressForm.lng,
          address: addressForm.address.trim(),
          buildingNumber: addressForm.buildingNumber.trim(),
          floorApartment: addressForm.floorApartment.trim(),
          image: addressForm.image,
        });
        onCancel?.();
      } else {
        toast.error(response?.data?.message || "Failed to update address");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update address");
    } finally {
      dispatch(changeLoader(false));
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const addressDetails = orderDetailData?.addressDetails || {};
    const pickupPointDetails = orderDetailData?.pickupPointDetails || {};

    const lat = addressDetails?.lat || pickupPointDetails?.latitude;
    const lng = addressDetails?.lng || pickupPointDetails?.longitude;
    const address = addressDetails?.address || pickupPointDetails?.address || "";
    const buildingNumber = addressDetails?.buildingNumber || pickupPointDetails?.buildingNumber || "";
    const floorApartment = addressDetails?.floorApartment || pickupPointDetails?.floorApartment || "";
    const image = addressDetails?.image || pickupPointDetails?.image || "";

    setAddressForm({
      lat: lat ? Number(lat) : null,
      lng: lng ? Number(lng) : null,
      address,
      buildingNumber,
      floorApartment,
      image,
    });
    setErrors({});
    setMapLinkError("");
    setMapLinkInput("");
    setIsMapsReady(false);
  }, [isOpen, orderDetailData]);

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={onCancel}
      width={1100}
      footer={null}
      destroyOnClose
      styles={{ body: { padding: 20 } }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 py-2">
        <div className="space-y-3">
          <div className="relative h-[320px] rounded-lg overflow-hidden border border-gray-200">
            <LoadScript
              googleMapsApiKey={config.GOOGLEMAPAPIKEY}
              libraries={["places"]}
              onLoad={() => setIsMapsReady(true)}
              onError={() => {
                setIsMapsReady(false);
                toast.error("Google Maps failed to load. Please refresh and try again.");
              }}
            >
              {isMapsReady ? (
                <>
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={
                      addressForm.lat && addressForm.lng
                        ? { lat: Number(addressForm.lat), lng: Number(addressForm.lng) }
                        : { lat: 24.7136, lng: 46.6753 }
                    }
                    zoom={14}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                    onClick={(event) => {
                      if (!isGoogleMapsAvailable()) {
                        toast.error("Google Maps is still loading. Please try again in a moment.");
                        return;
                      }

                      const lat = event.latLng.lat();
                      const lng = event.latLng.lng();
                      updateAddressCoordinates(lat, lng);
                      reverseGeocode(lat, lng);
                      checkAddressInZone(lat, lng);
                    }}
                    options={{
                      gestureHandling: "greedy",
                      zoomControl: true,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    {addressForm.lat && addressForm.lng ? (
                      <Marker
                        position={{ lat: Number(addressForm.lat), lng: Number(addressForm.lng) }}
                        draggable
                        onDragEnd={(event) => {
                          if (!isGoogleMapsAvailable()) {
                            toast.error("Google Maps is still loading. Please try again in a moment.");
                            return;
                          }

                          const lat = event.latLng.lat();
                          const lng = event.latLng.lng();
                          updateAddressCoordinates(lat, lng);
                          reverseGeocode(lat, lng);
                          checkAddressInZone(lat, lng);
                        }}
                      />
                    ) : null}
                  </GoogleMap>

                  <div className="absolute top-2 left-2 right-2 z-10">
                    <Autocomplete
                      onLoad={(autocomplete) => {
                        autocompleteRef.current = autocomplete;
                      }}
                      options={{ componentRestrictions: { country: "SA" } }}
                      onPlaceChanged={handlePlaceSelect}
                    >
                      <Input placeholder="Find address in Saudi Arabia" className="shadow-sm" />
                    </Autocomplete>
                  </div>

                  <div className="absolute right-3 top-[58px] z-10">
                    <Button
                      className="!rounded-full !h-10 !w-10 !px-0 !shadow-md"
                      type="default"
                      onClick={() => {
                        if (!navigator.geolocation) {
                          toast.error("Geolocation is not supported by this browser.");
                          return;
                        }

                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            const { latitude, longitude } = position.coords;
                            updateAddressCoordinates(latitude, longitude);
                            reverseGeocode(latitude, longitude);
                            checkAddressInZone(latitude, longitude);
                          },
                          () => toast.error("Unable to access your current location")
                        );
                      }}
                    >
                      <EnvironmentOutlined />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-500">
                  Loading Google Maps...
                </div>
              )}
            </LoadScript>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-semibold">Or paste a Google Maps link</div>
            <div className="flex gap-2">
              <Input
                placeholder="https://www.google.com/maps/place/..."
                value={mapLinkInput}
                onChange={(e) => {
                  setMapLinkInput(e.target.value);
                  if (mapLinkError) setMapLinkError("");
                }}
              />
              <Button onClick={handleMapLinkApply} type="primary" disabled={!mapLinkInput.trim()}>
                Apply
              </Button>
            </div>
            {mapLinkError ? <div className="text-red-500 text-xs">{mapLinkError}</div> : null}
          </div>
        </div>

        <div className="space-y-4 flex flex-col justify-start">
          <div className="rounded-lg ">
            <div className="text-sm font-semibold mb-2 text-[#222222]">Street Address</div>
            <div className="text-sm text-gray-700  break-words rounded-md bg-white">
              {addressForm.address || "No address selected"}
            </div>
            {errors.address ? <div className="text-red-500 text-xs mt-1">{errors.address}</div> : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#222222]">Building Number</label>
            <Input
              size="large"
              className="!h-12 !text-base"
              value={addressForm.buildingNumber}
              onChange={(e) => setFieldValue("buildingNumber", e.target.value)}
              status={errors.buildingNumber ? "error" : ""}
            />
            {errors.buildingNumber ? <div className="text-red-500 text-xs">{errors.buildingNumber}</div> : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#222222]">Floor / Apartment</label>
            <Input
              size="large"
              className="!h-12 !text-base"
              value={addressForm.floorApartment}
              onChange={(e) => setFieldValue("floorApartment", e.target.value)}
              status={errors.floorApartment ? "error" : ""}
            />
            {errors.floorApartment ? <div className="text-red-500 text-xs">{errors.floorApartment}</div> : null}
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-semibold mb-3 text-[#222222]">Upload Image</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button type="dashed" size="large" htmlType="button" className="!h-12">
                <label className="cursor-pointer m-0 font-medium">
                  Upload
                  <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </label>
              </Button>
              {addressForm.image ? (
                <img
                  src={addressForm.image.startsWith("http") ? addressForm.image : `${config.AWS_URL}${addressForm.image}`}
                  alt="Uploaded location"
                  className="h-20 w-20 rounded-lg object-cover border border-gray-200 bg-white"
                />
              ) : null}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSave}>Save</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeAddressModal;
